import Promise from 'bluebird';
import _ from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { serializeImageAssetMetadata } from '@bingads-webui-campaign/media-metadata-model';
import { getExpandedImageUrl as getExpandedImageUrlUAPL, setROIInfo } from '@bingads-webui-campaign-react/unified-ad-preview-library-util';
import C from './constants';

/**
 * ImageExperienceImage Class
 * This class is used internally to the ImageExperience pacakge
 * Main purpose is to have a consistant object across different components
 *
 * @class ImageExperienceImage
 */
class ImageExperienceImage {
  /**
   * Creates an instance of ImageExperienceImage.
   *
   * @param {Object} imageObj - image object
   * @param {id} imageObj.id - id of the image
   * @param {String} imageObj.idString - id of the image
   * @param {Number} imageObj.width - Width of the image
   * @param {Number} imageObj.heigth - Height of the image
   * @param {string} imageObj.type - Type of the image
   * @param {string} imageObj.associationType - Sub type of the image
   * @param {string} imageObj.text - Text of the image
   * @param {string} imageObj.altText - Alt Text of the image
   * @param {Object} imageObj.cropSettings - Crop setting object of the image
   * @param {Object} imageObj.effectSettings - Effect setting object of the image
   * @param {string} imageObj.baseId - base id of the image without metadata
   * @param {string} imageObj.metadata - string metadata of the image, e.g. effect info
   * @param {Boolean} imageObj.isIncluded - Is the aspect ratio included
   * @param {Boolean} imageObj.isLogo - Is a logo aspect ratio
   * @param {Boolean} imageObj.isPortrait - Is a portrait aspect ratio
   * @param {Boolean} imageObj.isRecommendedImage - Is an recommended image
   * @param {Boolean} imageObj.disableEdit - disable image edit
   * @param {Boolean} createBaseImageInfo - Whether to create base image info
   * @param {Object} imageObj.originCropSetting - cropsetting without manual cropping
   * @param {boolean} imageObj.isAutoCropping - auto cropping
   * @param {string} imageObj.source - source of image (e.g. website images, carousel, etc.)
   * @param {string} imageObj.originalUrl - original url of image if user has done a bulk crop
   * @param {string} imageObj.cropSettingOnOriginal - crop setting to derive current image
   * from original
   * @param {array} imageObj.placementSettings - placement settings if bulk crop was done
   *  image source (Website, Upload, Existing or Stock)
   * @param {number} imageObj.originalHeight - original height of image pre-bulk crop
   * @param {number} imageObj.originalWidth - original width of the image pre-bulk crop
   *
   * @param {string} imageObj.imageExpansionType - image expansion type, constants.ImageExpansionType
   * @param {Object} imageObj.originalImageCropSettings - {SourceX, SourceY, SourceWidth, SourceHeight}
   * @param {Object} imageObj.expandedImageCropSettings - {SourceX, SourceY, SourceWidth, SourceHeight}
   * @param {string} imageObj.expandedImageUrl - expanded image url, for outpainting only, and only for return from fitting api
   * @param {number} expandedImageWidth - expanded image width
   * @param {number} expandedImageHeight - expanded image height
   * @param {string} paddingColor - padding color for single color padding
   */
  constructor(imageObj, createBaseImageInfo = true) {
    this.id = imageObj.id;
    this.idString = imageObj.idString;
    this.src = imageObj.src;
    this.type = imageObj.type;
    this.associationType = imageObj.associationType;
    this.width = imageObj.width;
    this.height = imageObj.height;
    this.cropSettings = imageObj.cropSettings;
    this.effectSettings = imageObj.effectSettings;
    this.baseId = imageObj.baseId;
    this.metadata = imageObj.metadata;
    this.isIncluded = imageObj.isIncluded;
    this.isLogo = imageObj.isLogo;
    this.isPortrait = imageObj.isPortrait;
    this.isRecommendedImage = imageObj.isRecommendedImage;
    this.disableEdit = imageObj.disableEdit;
    this.originCropSetting = imageObj.originCropSetting || this.cropSettings;
    this.isAutoCropping = imageObj.isAutoCropping;
    this.text = imageObj.text;
    this.altText = imageObj.altText;
    this.source = imageObj.source;
    this.originalSourceType = imageObj.originalSourceType;
    this.originalUrl = imageObj.originalUrl;
    this.cropSettingOnOriginal = imageObj.cropSettingOnOriginal;
    this.placementSettings = imageObj.placementSettings;
    this.originalHeight = imageObj.originalHeight;
    this.originalWidth = imageObj.originalWidth;

    // constants.ImageExpansionType
    this.imageExpansionType = imageObj.imageExpansionType;
    this.expandedImageCropSettings = imageObj.expandedImageCropSettings;
    // expanded image url from imageFitting response, for outpainting only, and it's the url on blob storage
    this.expandedImageUrl = imageObj.expandedImageUrl;
    this.expandedImageWidth = imageObj.expandedImageWidth;
    this.expandedImageHeight = imageObj.expandedImageHeight;
    this.cropSettings = this.cropSettings || imageObj.originalImageCropSettings || null;
    this.originalImageCropSettings = imageObj.originalImageCropSettings;
    this.paddingColor = imageObj.paddingColor;

    if (createBaseImageInfo && this.type) {
      // parent-child relationship will only be one level deep
      this.baseImage = new ImageExperienceImage(_.defaults({
        cropSettings: null,
        effectSettings: null,
        type: null,
        id: this.BaseId,
        baseId: null,
        metadata: null,
        // don't keep expansion cropping info in baseImage
        expandedImageCropSettings: null,
        originalImageCropSettings: null,
      }, imageObj), false /* createBaseImageInfo */);
    } else {
      this.baseImage = imageObj.baseImage;
    }
  }

  /**
   * Get base image
   *
   * @readonly
   * @returns {Number} - base image
   */
  get BaseImage() {
    return this.baseImage;
  }

  setBaseImage(baseImage) {
    this.baseImage = baseImage;
  }

  /**
   * Get source of the image
   *
   * @readonly
   * @returns {Number} - id of the saved image
   */
  get Id() {
    return this.id;
  }

  /**
   * Get base source of the image without metadata
   *
   * @readonly
   * @returns {String} - id of base image.
   */
  get BaseId() {
    // if baseId is not set in constructor, this is already original image, return Id instead.
    return this.baseId || this.Id;
  }

  /**
   * Get source of the image
   *
   * @readonly
   * @returns {string} - id of the saved image
   */
  get IdString() {
    return this.idString;
  }

  /**
   * Get source of the image
   *
   * @readonly
   * @returns {string} - string source of the image either base64 or url
   */
  get Src() {
    return this.src;
  }

  setSrc(src) {
    this.src = src;
  }

  /**
   * Get metadata of the image
   *
   * @readonly
   * @returns {string} - Metadata associated with this image
   */
  get Metadata() {
    return this.metadata;
  }

  /**
   * Get width of the image
   *
   * @readonly
   * @returns {Number} - Width of the image
   */
  get Width() {
    return this.width;
  }

  /**
   * Get height of the image
   *
   * @readonly
   * @returns {Number} - Height of the image
   */
  get Height() {
    return this.height;
  }

  /**
   * Get type of the image
   *
   * @readonly
   * @returns {string} - Type of the image
   */
  get Type() {
    return this.type;
  }

  /**
   * Get text of the image
   *
   * @readonly
   * @returns {string} - Text of the image
   */
  get Text() {
    return this.text;
  }

  get AltText() {
    return this.altText;
  }

  setType(type) {
    this.type = type;
  }

  /**
   * Get crop settings of the image
   *
   * @readonly
   * @returns {Object} - crop settings of the image
   */
  get CropSettings() {
    return this.cropSettings;
  }

  /**
   * Get effect settings of the image
   *
   * @readonly
   * @returns {Object} - crop settings of the image
   */
  get EffectSettings() {
    return this.effectSettings;
  }

  setEffectSettings(effectSettings) {
    this.effectSettings = effectSettings;
  }

  isMaunalCropped() {
    return !_.isEqual(this.originCropSetting, this.cropSettings);
  }

  isEffectEdited() {
    const metadata = serializeImageAssetMetadata({ effectSettings: this.EffectSettings });

    if (_.isEmpty(this.Metadata) && _.isEmpty(metadata)) {
      return false;
    }

    return !_.isEqual(this.Metadata, metadata);
  }

  isManualEdited(isAdCreatorEnabled = false) {
    if (!isAdCreatorEnabled) {
      return this.isMaunalCropped();
    }

    return this.isEffectEdited() || this.isMaunalCropped();
  }

  setIncluded(value) {
    this.isIncluded = value;
  }

  setAutoCropping(value) {
    this.isAutoCropping = value;
  }

  /**
   * Get final src of the image with effect if any.
   *
   * @readonly
   * @returns {string} - final image src with effect
   */
  get FinalSrc() {
    if (this.effectSettings && this.effectSettings.effectKey) {
      return this.effectSettings.effectSrc;
    }
    return this.src;
  }

  /**
   * Get if the aspect ratio is included
   *
   * @readonly
   * @returns {Boolean} - is default ratio of the image
   */
  get IsIncluded() {
    return this.isIncluded;
  }

  /**
   * Get crop settings of the image
   *
   * @readonly
   * @returns {string} - thumbnail url for the AdList
   */
  get ThumbnailForAdList() {
    return `${this.ThumbnailUrl}&w=191&h=100`;
  }

  get ThumbnailUrl() {
    const srcWithQuery = this.src.indexOf('?') > 0 ? this.src : `${this.src}?`;
    let cropQuery = '';

    if (this.cropSettings) {
      const left = this.cropSettings.SourceX / this.width;
      const top = this.cropSettings.SourceY / this.height;
      const right = (this.cropSettings.SourceX + this.cropSettings.SourceWidth) / this.width;
      const bottom = (this.cropSettings.SourceY + this.cropSettings.SourceHeight) / this.height;

      cropQuery = `&c=16&roil=${left}&roit=${top}&roir=${right}&roib=${bottom}`;
    }

    return `${srcWithQuery}${cropQuery}`;
  }

  copyThumbnailLookToNewImage({ id, src }) {
    return new ImageExperienceImage({
      id,
      src,
      cropSettings: cloneDeep(this.CropSettings),
      width: this.Width,
      height: this.Height,
    });
  }

  // they are coming from same base image, but with different look
  copyEffectsFromOther(another) {
    const fields = [
      'id',
      'idString',
      'src',
      'effectSettings',
      'baseId',
      'metadata',
      'isLogo',
      'isPortrait',
      'isRecommendedImage',
      'text',
      'source',
    ];
    _.extend(this, _.pick(another, fields));
  }

  /**
   * Get source of the image, either Website, Upload, Existing or Stock
   *
   * @readonly
   * @returns {string} - thumbnail url for the AdList
   */
  get Source() {
    return this.source;
  }

  get OriginalSourceType() {
    return this.originalSourceType;
  }

  get OriginalUrl() {
    return this.originalUrl;
  }

  get PlacementSettings() {
    return this.placementSettings;
  }

  get OriginalHeight() {
    return this.originalHeight;
  }

  get OriginalWidth() {
    return this.originalWidth;
  }

  get CropSettingOnOriginal() {
    return this.cropSettingOnOriginal;
  }

  get IsBulkCropped() {
    return this.Src !== this.OriginalUrl;
  }

  setCropSettings(cropSettings) {
    this.cropSettings = cropSettings;
  }

  setAltText(text) {
    this.altText = text;
  }

  // for some recommend ads, id of ImageExperienceImage could be null
  setNewId(id) {
    this.id = id;
    this.idString = String(id);
  }

  setAssociationType(associationType) {
    this.associationType = associationType;
  }

  // if the image is processed by imageFitting api, no matter if it's be able to be expaneded.
  get isImageExpanded() {
    return !!this.imageExpansionType;
  }

  getExpandedImageUrl() {
    return getExpandedImageUrlUAPL(this.imageExpansionType, this.src, this.paddingColor, this.expandedImageWidth, this.expandedImageHeight);
  }

  /**
   * Get the clone of this object
   *
   * @returns {ImageExperienceImage} - cloned ImageExperienceImage
   */
  clone() {
    const base = this.baseImage ? new ImageExperienceImage(cloneDeep({
      ...this.baseImage,
    }), false) : null;

    return new ImageExperienceImage(_.extend(cloneDeep({
      id: this.id,
      idString: this.idString,
      src: this.src,
      type: this.type,
      associationType: this.associationType,
      width: this.width,
      height: this.height,
      altText: this.altText,
      cropSettings: this.cropSettings,
      effectSettings: this.effectSettings,
      isIncluded: this.isIncluded,
      isLogo: this.isLogo,
      isPortrait: this.isPortrait,
      isRecommendedImage: this.isRecommendedImage,
      disableEdit: this.disableEdit,
      baseId: this.baseId,
      metadata: this.metadata,
      source: this.source,
      originalSourceType: this.originalSourceType,
      originalUrl: this.originalUrl,
      originalWidth: this.originalWidth,
      originalHeight: this.originalHeight,
      cropSettingOnOriginal: this.cropSettingOnOriginal,
      placementSettings: this.placementSettings,

      // image fitting info
      imageExpansionType: this.imageExpansionType,
      expandedImageCropSettings: this.expandedImageCropSettings,
      expandedImageUrl: this.expandedImageUrl,
      expandedImageWidth: this.expandedImageWidth,
      expandedImageHeight: this.expandedImageHeight,
      paddingColor: this.paddingColor,
      originalImageCropSettings: this.originalImageCropSettings,
    }), {
      baseImage: base,
    }), false);
  }

  copyExpandedImageInfo(expandedImage) {
    if (expandedImage && expandedImage.isImageExpanded) {
      this.imageExpansionType = expandedImage.imageExpansionType;
      this.expandedImageCropSettings = expandedImage.expandedImageCropSettings;
      this.expandedImageUrl = expandedImage.expandedImageUrl;
      this.expandedImageWidth = expandedImage.expandedImageWidth;
      this.expandedImageHeight = expandedImage.expandedImageHeight;
      this.paddingColor = expandedImage.paddingColor;
      this.originalImageCropSettings = expandedImage.originalImageCropSettings;
    }
  }

  setROIInfo(roiInfoStr, isMMAV2Enabled = false, isPlacementLevel = false) {
    if (_.isEmpty(roiInfoStr)) {
      return;
    }

    const {
      expandedImageWidth,
      expandedImageHeight,
      expandedImageCropSettings,
      originalImageCropSettings,
      imageExpansionType,
      paddingColor,
    } = setROIInfo(roiInfoStr, this.src);

    this.imageExpansionType = imageExpansionType;
    this.expandedImageWidth = expandedImageWidth;
    this.expandedImageHeight = expandedImageHeight;
    this.expandedImageCropSettings = expandedImageCropSettings;
    this.originalImageCropSettings = originalImageCropSettings;
    this.paddingColor = paddingColor;

    if (isPlacementLevel) {
      let aspectRatio = C.aspectRatios[this.type] && C.aspectRatios[this.type].aspectRatio;
      if (isMMAV2Enabled) {
        aspectRatio = C.aspectRatiosForMMAV2[this.type] && C.aspectRatiosForMMAV2[this.type].aspectRatio;
      }
      const ecsForCurAspectRatio = this.expandedImageCropSettings ? _.filter(this.expandedImageCropSettings, ({ AspectRatio: ar }) => ar === aspectRatio) : [];
      if (ecsForCurAspectRatio.length > 0) {
        this.expandedImageCropSettings = {
          SourceHeight: ecsForCurAspectRatio[0].CropSetting.SourceHeight,
          SourceWidth: ecsForCurAspectRatio[0].CropSetting.SourceWidth,
          SourceX: ecsForCurAspectRatio[0].CropSetting.SourceX,
          SourceY: ecsForCurAspectRatio[0].CropSetting.SourceY,
        };
      } else {
        this.expandedImageCropSettings = undefined;
      }

      const originalEcsForCurAspectRatio = this.originalImageCropSettings ? _.filter(this.originalImageCropSettings, ({ AspectRatio: ar }) => ar === aspectRatio) : [];
      if (originalEcsForCurAspectRatio.length > 0) {
        // save croppingType as CenterCrop for Outpainting and OriginalImage
        const croppingType = this.imageExpansionType === C.ImageExpansionType.Outpainting || this.type === 'OriginalImage' ? C.CroppingType.CenterCrop : C.CroppingType.SmartCrop;
        this.originalImageCropSettings = {
          SourceHeight: originalEcsForCurAspectRatio[0].CropSetting.SourceHeight,
          SourceWidth: originalEcsForCurAspectRatio[0].CropSetting.SourceWidth,
          SourceX: originalEcsForCurAspectRatio[0].CropSetting.SourceX,
          SourceY: originalEcsForCurAspectRatio[0].CropSetting.SourceY,
          CroppingType: croppingType,
        };
        if (this.cropSettings && this.cropSettings.CroppingType !== C.CroppingType.ManualCrop) {
          this.cropSettings = this.originalImageCropSettings;
        }
      }
    }
  }

  getBasicData() {
    return {
      id: this.id,
      idString: this.idString,
      src: this.src,
      width: this.width,
      height: this.height,
      text: this.text,
      altText: this.altText,
      type: this.type,
      isIncluded: this.isIncluded,
      source: this.source,
      originalSourceType: this.originalSourceType,
    };
  }

  /**
   * Get the object formatted in JSON to save
   *
   * @param {Boolean} isMultimediaAd - Is multimedia ad
   * @param {Boolean} isVideoThumbnail - Is this image used for video thumbnail
   * @param {Boolean} overrideInfo - If field exists, take this isntead of member field.
   * @param {Boolean} isMMAV2Enabled - If field exists, take this isntead of member field.
   * @returns {Object} - Object formatted to be saved on MT
   */
  formatToSave(
    isMultimediaAd = false,
    isVideoThumbnail = false,
    overrideInfo = {},
    isMMAV2Enabled = false,
    shouldSaveCroppingType = false
  ) {
    let subType;

    if (isVideoThumbnail) {
      subType = this.Type;
    } else {
      /* eslint-disable no-lonely-if */
      if (this.associationType) {
        subType = this.associationType;
      } else {
        subType = isMultimediaAd ?
          C.getMmaAspectRatios()[this.Type].associationType :
          C.aspectRatios[this.Type] && C.aspectRatios[this.Type].associationType;
        if (isMMAV2Enabled) {
          subType = C.aspectRatiosForMMAV2[this.Type] && C.aspectRatiosForMMAV2[this.Type].associationType;
        }
      }
    }

    let result = _.extend({}, _.isEmpty(overrideInfo) ? {
      id: this.Id,
      idString: this.IdString || this.Id.toString(),
    } : {
      id: overrideInfo.id,
      idString: overrideInfo.id.toString(),
    }, { subType });

    if (this.cropSettings) {
      result = {
        ...result,
        cropX: this.cropSettings.SourceX,
        cropY: this.cropSettings.SourceY,
        cropWidth: this.cropSettings.SourceWidth,
        cropHeight: this.cropSettings.SourceHeight,
      };
      if (shouldSaveCroppingType && this.cropSettings.CroppingType) {
        result = {
          ...result,
          croppingType: this.cropSettings.CroppingType,
        };
      }
    }

    return result;
  }
}

/**
 * Returns a promise of returning ImageExperienceImage
 *
 * @param {string} imageSource - string representation of image source
 * @returns {Promise} - promise of returning loaded ImageExperienceImage
 */
const newImageFromSourceAsync = (imageSource) => {
  const nativeImage = new Image();

  nativeImage.src = imageSource;

  let imageLoadResolve = null;

  const imageLoadPromise = new Promise((resolve) => {
    imageLoadResolve = resolve;
  });

  nativeImage.onload = () => {
    const imageObj = new ImageExperienceImage({
      src: nativeImage.src,
      width: nativeImage.naturalWidth,
      height: nativeImage.naturalHeight,
      cropSettings: {
        SourceX: 0,
        SourceY: 0,
        SourceWidth: nativeImage.naturalWidth,
        SourceHeight: nativeImage.naturalHeight,
      },
    });

    imageLoadResolve(imageObj);
  };

  return imageLoadPromise;
};

/**
 * Returns a {ImageExperienceImage} object
 *
 * @param {object} imageObjectMT - object returned from MT for the image
 * @param {string} source - source of the image
 * @returns {ImageExperienceImage} - ImageExperienceImage object
 */
const newImageFromMTObject = (imageObjectMT, source) => {
  const imageExpansionType = imageObjectMT.ImageExpansionType || imageObjectMT.imageExpansionType;
  const cropSettings = imageObjectMT.OriginalImageCroppingSettings || imageObjectMT.originalImageCroppingSettings;
  const imageObj = new ImageExperienceImage({
    id: imageObjectMT.Id || imageObjectMT.id,
    idString: imageObjectMT.IdString || imageObjectMT.idString,
    src: imageObjectMT.Url || imageObjectMT.url,
    width: imageObjectMT.Width || imageObjectMT.width,
    height: imageObjectMT.Height || imageObjectMT.height,
    text: imageObjectMT.Text || imageObjectMT.text,
    altText: imageObjectMT.AltText || imageObjectMT.altText,
    cropSettingOnOriginal: imageObjectMT.CropSettingOnOriginal || imageObjectMT.cropSettingOnOriginal,
    originalUrl: imageObjectMT.OriginalUrl || imageObjectMT.originalUrl,
    placementSettings: imageObjectMT.PlacementSettings || imageObjectMT.placementSettings,
    originalHeight: imageObjectMT.OriginalHeight || imageObjectMT.originalHeight,
    originalWidth: imageObjectMT.OriginalWidth || imageObjectMT.originalWidth,
    metadata: imageObjectMT.Metadata || imageObjectMT.metadata,
    source,
    originalSourceType: imageObjectMT.SourceType || imageObjectMT.sourceType,
    // image fitting info
    imageExpansionType,
    expandedImageCropSettings: imageObjectMT.ExpandedImageCroppingSettings || imageObjectMT.expandedImageCropSettings,
    expandedImageUrl: imageObjectMT.ExpandedImageUrl || imageObjectMT.expandedImageUrl,
    expandedImageWidth: imageObjectMT.ExpandedImageWidth || imageObjectMT.expandedImageWidth,
    expandedImageHeight: imageObjectMT.ExpandedImageHeight || imageObjectMT.expandedImageHeight,
    paddingColor: imageObjectMT.PaddingColor || imageObjectMT.paddingColor,
    cropSettings,
    originalImageCropSettings: imageObjectMT.OriginalImageCropSettings || imageObjectMT.originalImageCropSettings,
  });

  if (imageObjectMT.ROIInfo) {
    imageObj.setROIInfo(imageObjectMT.ROIInfo);
  }

  return imageObj;
};

export {
  ImageExperienceImage,
  newImageFromSourceAsync,
  newImageFromMTObject,
};
